import { useCallback, useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import { Loader } from "semantic-ui-react"
import { getLocalStorageItem } from "simplydo/core/helpers"
import { canManageChallenge } from "simplydo/core/permissions"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import { useAppSelector } from "store"

import { ImpactStatementEditor } from "./ImpactStatementEditor"

type Challenge = OpenAPI.GET<"/challenges/{id}">["response"]
type ImpactStatements = OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]

type ImpactStatementProps = {
  statementDefintion: Challenge["impactStatements"]
  forStage: OpenAPI.Schemas["ImpactStatement"]["stage"]
  idea: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][0]
  onUpdate?: (statement: ImpactStatements) => void
  editable?: boolean
  canManageIdea?: boolean
}

const IdeaImpactStatement = ({ statementDefintion, forStage, idea, onUpdate, editable }: ImpactStatementProps) => {
  const ideaId = idea?._id
  const user = useAppSelector((state) => state.user)
  const { projectManagement = {} } = idea
  const { boards = [] } = projectManagement
  const pmBoardObject = boards.length ? boards.find((b) => b.forId === idea?.ownerChallenge?._id) : null
  const userIsAssigned = useMemo(() => {
    const isAssigned = (pmBoardObject?.assignees || []).find((assigneeId) => assigneeId === user._id)
    return !!isAssigned
  }, [user, pmBoardObject])
  const userIsAssessor = useMemo(
    () => !!(idea?.assessments || []).find((assessment) => assessment?.user === user._id),
    [idea, user],
  )

  const canManageIdea = canManageChallenge(user, idea.ownerChallenge) || userIsAssigned

  const authToken = getLocalStorageItem(`ideaAuthToken:${ideaId}`)

  const impactStatementQuery = useQuery({
    queryKey: ["ideas", idea._id, "impactStatement"],
    queryFn: asQuery(api.ideas.getImpactStatements, {
      params: [idea._id, authToken],
    })!,
  })

  const updateImpacts = useCallback(
    (impactStatements: ImpactStatements) => {
      api.queryClient.setQueryData(
        ["ideas", ideaId, "impactStatement"],
        (prev: { impactStatements: ImpactStatements }) => ({
          impactStatements: {
            ...(prev.impactStatements || {}),
            ...impactStatements,
          },
        }),
      )
      onUpdate?.(impactStatements)
    },
    [onUpdate, ideaId],
  )

  if (impactStatementQuery.isLoading) {
    return <Loader active inline="centered" />
  }

  return (
    <ImpactStatementEditor
      statementDefinition={impactStatementQuery.data?.statementDefinition}
      impactStatements={impactStatementQuery.data?.impactStatements || {}}
      setImpactStatements={updateImpacts}
      statementDefintion={statementDefintion}
      ideaId={ideaId!}
      challengeId={idea.challenge!}
      editable={editable}
      canManageIdea={canManageIdea}
      restrictStagesTo={
        canManageIdea
          ? undefined
          : userIsAssessor
            ? statementDefintion?.assessorVisibility || ["userEstimate"]
            : ["userEstimate"]
      }
      forStage={forStage}
    />
  )
}

export default IdeaImpactStatement
